import type { Coordinate } from './geo';

export const getPointPositionOnQuadraticBezierCurve = (
  start: Coordinate,
  control: Coordinate,
  end: Coordinate,
  segmentPointPosition: number
): Coordinate => {
  const StartPointMultiplier = (segmentPointPosition: number): number =>
    (1 - segmentPointPosition) * (1 - segmentPointPosition);

  const ControlPointMultiplier = (segmentPointPosition: number): number =>
    2 * segmentPointPosition * (1 - segmentPointPosition);

  const EndPointMultiplier = (segmentPointPosition: number): number =>
    segmentPointPosition * segmentPointPosition;

  return {
    latitude:
      StartPointMultiplier(segmentPointPosition) * start.latitude +
      ControlPointMultiplier(segmentPointPosition) * control.latitude +
      EndPointMultiplier(segmentPointPosition) * end.latitude,
    longitude:
      StartPointMultiplier(segmentPointPosition) * start.longitude +
      ControlPointMultiplier(segmentPointPosition) * control.longitude +
      EndPointMultiplier(segmentPointPosition) * end.longitude,
  };
};

export function getCubicBezierControlPoint(
  startCoordinate: Coordinate,
  endCoordinate: Coordinate
): { latitude: number; longitude: number } {
  const start = new google.maps.LatLng(
    startCoordinate.latitude,
    startCoordinate.longitude
  );
  const end = new google.maps.LatLng(
    endCoordinate.latitude,
    endCoordinate.longitude
  );
  const startEndDistance =
    google.maps.geometry.spherical.computeDistanceBetween(start, end);
  const distanceToCurvePoint = getDistanceToCurvePoint(startEndDistance);
  const curvatureCoefficent = getCurvatureCoefficient(distanceToCurvePoint);

  const initialHeadingFromStart = google.maps.geometry.spherical.computeHeading(
    start,
    end
  );
  const midPointCoordinate = google.maps.geometry.spherical.computeOffset(
    start,
    distanceToCurvePoint,
    initialHeadingFromStart
  );

  const angleStartEndVectorToCurvePointVector = 90;
  const controlPointLatLng = google.maps.geometry.spherical.computeOffset(
    midPointCoordinate,
    curvatureCoefficent,
    initialHeadingFromStart + angleStartEndVectorToCurvePointVector
  );
  const controlPointCoordinate = {
    longitude: controlPointLatLng.lng(),
    latitude: controlPointLatLng.lat(),
  };
  return controlPointCoordinate;
}

export function getDistanceToCurvePoint(startEndDistance: number): number {
  const coefficient = 1.5;
  const CURVATURE_DISTANCE_BREAKPOINT = 5000000;
  return startEndDistance > CURVATURE_DISTANCE_BREAKPOINT
    ? startEndDistance / coefficient
    : startEndDistance / 2;
}

export function getCurvatureCoefficient(distanceToCurvePoint: number): number {
  const coefficient = 1.6;
  const CURVATURE_ANGLE = Math.PI / coefficient;
  return distanceToCurvePoint * Math.cos(CURVATURE_ANGLE);
}
